import { Component, EventEmitter, Input, Output, ViewChild, ElementRef, OnInit } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { faUpload, faFile, faTimes, faCheck, faPlus, faTag, faSearch } from '@fortawesome/free-solid-svg-icons';
import { ArchivesService } from '../../services/archives.service';
import { TagsService, Tag } from '../../services/tags.service';
import { AlertService } from '../alert-service/alert-service';
import { normalizeForSearch } from '../../utils/text-search.util';

@Component({
  selector: 'app-modal-upload-archives',
  standalone: true,
  imports: [
    FormsModule,
    FontAwesomeModule,
    TranslateModule
],
  templateUrl: './modal-upload-archives.component.html',
  styleUrls: ['./modal-upload-archives.component.scss']
})
export class ModalUploadArchivesComponent implements OnInit {
  @Input() show = false;
  @Output() close = new EventEmitter<void>();
  @Output() uploadSuccess = new EventEmitter<string[]>();

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('folderInput') folderInput!: ElementRef<HTMLInputElement>;

  selectedFiles: File[] = [];
  uploadLoading = false;
  uploadProgress: { current: number; total: number } | null = null;

  readonly MAX_TAGS = 3;
  availableTags: Tag[] = [];
  filteredAvailableTags: Tag[] = [];
  selectedTags: Tag[] = [];
  showTagDropdown = false;
  tagSearchTerm = '';

  faUpload = faUpload;
  faFile = faFile;
  faTimes = faTimes;
  faCheck = faCheck;
  faPlus = faPlus;
  faTag = faTag;
  faSearch = faSearch;

  constructor(
    private archivesService: ArchivesService,
    private tagsService: TagsService,
    private translate: TranslateService,
    private alert: AlertService
  ) {}

  ngOnInit(): void {
    this.loadTags();
  }

  private loadTags(): void {
    this.tagsService.getTags().subscribe({
      next: (response) => {
        this.availableTags = response.tags || [];
        this.filteredAvailableTags = [...this.availableTags];
      },
      error: () => {
        this.availableTags = [];
        this.filteredAvailableTags = [];
      }
    });
  }

  toggleTagDropdown(): void {
    this.showTagDropdown = !this.showTagDropdown;
    if (this.showTagDropdown) {
      this.tagSearchTerm = '';
      this.filteredAvailableTags = [...this.availableTags];
    }
  }

  filterTags(): void {
    const term = normalizeForSearch(this.tagSearchTerm);
    this.filteredAvailableTags = term
      ? this.availableTags.filter(tag => normalizeForSearch(tag.name).includes(term))
      : [...this.availableTags];
  }

  isTagSelected(tag: Tag): boolean {
    return this.selectedTags.some(t => t.id === tag.id);
  }

  selectTag(tag: Tag): void {
    if (this.isTagSelected(tag) || this.selectedTags.length >= this.MAX_TAGS) return;
    this.selectedTags = [...this.selectedTags, tag];
    if (this.selectedTags.length >= this.MAX_TAGS) {
      this.showTagDropdown = false;
    }
  }

  removeTag(tag: Tag): void {
    this.selectedTags = this.selectedTags.filter(t => t.id !== tag.id);
  }

  onClose(): void {
    this.close.emit();
    this.resetModal();
  }

  triggerFileInput(): void {
    this.fileInput?.nativeElement?.click();
  }

  triggerFolderInput(): void {
    this.folderInput?.nativeElement?.click();
  }

  onFileSelected(ev: Event): void {
    const input = ev.target as HTMLInputElement;
    if (input.files && input.files.length) {
      this.selectedFiles = [...this.selectedFiles, ...Array.from(input.files)];
    }
  }

  onFolderSelected(ev: Event): void {
    const input = ev.target as HTMLInputElement;
    if (input.files && input.files.length) {
      this.selectedFiles = [...this.selectedFiles, ...Array.from(input.files)];
    }
  }

  removeFile(idx: number): void {
    this.selectedFiles.splice(idx, 1);
  }

  clearSelectedFiles(): void {
    this.selectedFiles = [];
  }

  confirmUpload(): void {
    if (this.selectedFiles.length === 0) return;

    this.uploadLoading = true;
    this.uploadProgress = { current: 0, total: this.selectedFiles.length };

    let uploadedCount = 0;
    let uploadedFileIds: string[] = [];

    this.archivesService.uploadInBatches(this.selectedFiles, this.selectedTags.map(t => t.id)).subscribe({
      next: (progress) => {
        uploadedCount = progress.uploaded;
        uploadedFileIds = this.extractUploadedFileIds(progress.archives);
        this.uploadProgress = { current: progress.uploaded, total: progress.total };
      },
      error: (err) => {
        this.uploadLoading = false;
        this.uploadProgress = null;
        this.alert.showError(err);
        if (uploadedCount > 0) {
          this.selectedFiles = this.selectedFiles.slice(uploadedCount);
          this.uploadSuccess.emit(uploadedFileIds);
        }
      },
      complete: () => {
        this.alert.showSuccess(this.translate.instant('ARCHIVES.UPLOAD.SUCCESS'));
        this.uploadSuccess.emit(uploadedFileIds);
        this.onClose();
      }
    });
  }

  private extractUploadedFileIds(response: any): string[] {
    if (Array.isArray(response)) {
      return response.map((file: any) => String(file.id || file.archive_id));
    }
    if (response && response.archives && Array.isArray(response.archives)) {
      return response.archives.map((file: any) => String(file.id || file.archive_id));
    }
    if (response && response.files && Array.isArray(response.files)) {
      return response.files.map((file: any) => String(file.id || file.archive_id));
    }
    return [];
  }

  private resetModal(): void {
    this.uploadLoading = false;
    this.uploadProgress = null;
    this.selectedFiles = [];
    this.selectedTags = [];
    this.showTagDropdown = false;
    this.tagSearchTerm = '';
    if (this.fileInput) this.fileInput.nativeElement.value = '';
    if (this.folderInput) this.folderInput.nativeElement.value = '';
  }
}